import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from 'components/Button/Button';
import Spin from 'components/Spin';
import Select from 'components/form/Select';
import { getDeviceSettings, saveDeviceSettings } from 'hooks/useMediaRecorder';
import React, { useState, useEffect } from 'react';
import toast from 'utils/toast';
import { TabContent } from './BottomTabs';
import MicrophoneLevel from './MicrophoneLevel';

const AudioSettings = ({ onClose, onDeviceChange }) => {
  const [devices, setDevices] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMic, setSelectedMic] = useState('');
  const [selectedSpeaker, setSelectedSpeaker] = useState('');
  const [micStream, setMicStream] = useState(null);
  const [isSinkSupported, setIsSinkSupported] = useState(false);

  useEffect(() => {
    navigator.mediaDevices.addEventListener('devicechange', getDevices);
    getDevices();

    const audio = new Audio();
    if (audio.setSinkId) {
      setIsSinkSupported(true);
    } else {
      setIsSinkSupported(false);
    }

    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', getDevices);
      if (micStream) {
        micStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const onSaveMic = (deviceId) => {
    const settings = getDeviceSettings() || {};
    settings.audioinput = deviceId;
    saveDeviceSettings(settings);
    onDeviceChange(settings);
    setSelectedMic(deviceId);
    startMicrophoneTest(deviceId);
  };

  const onSaveSpeaker = (deviceId) => {
    const settings = getDeviceSettings() || {};
    settings.audiooutput = deviceId;
    saveDeviceSettings(settings);
    onDeviceChange(settings);
    setSelectedSpeaker(deviceId);
  };

  const getDevices = async () => {
    setIsLoading(true);
    try {
      const { audioinput, audiooutput } = getDeviceSettings() || {};

      if (audioinput) {
        setSelectedMic(audioinput);
      }
      if (audiooutput) {
        setSelectedSpeaker(audiooutput);
      }

      // Request microphone permission first to ensure we get labeled devices
      await navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          // Store this stream temporarily
          const tracks = stream.getTracks();
          // We can stop the tracks immediately after getting permission
          tracks.forEach((track) => track.stop());
        })
        .catch((err) => {
          console.error('Error requesting initial mic permission:', err);
          toast.error('Please allow microphone access to use this feature');
        });

      // Now enumerate devices after getting permission
      const deviceList = await navigator.mediaDevices.enumerateDevices();

      // Filter and set devices
      const audioInputs = deviceList.filter(
        (device) => device.kind === 'audioinput',
      );

      // For MacOS, we need to handle speakers differently
      // Default output device might not be listed in enumerateDevices on some browsers/platforms
      let audioOutputs = deviceList.filter(
        (device) => device.kind === 'audiooutput',
      );

      // Add a default device if no speakers were found (for MacBooks, etc.)
      if (audioOutputs.length === 0) {
        // Create a custom MediaDeviceInfo object with toJSON method
        const defaultSpeaker = {
          deviceId: 'default',
          kind: 'audiooutput',
          label: 'System Default (Built-in Speakers)',
          groupId: 'default',
          toJSON: function () {
            return {
              deviceId: this.deviceId,
              kind: this.kind,
              label: this.label,
              groupId: this.groupId,
            };
          },
        };

        audioOutputs = [defaultSpeaker];
      }

      setDevices({
        audioInputs,
        audioOutputs,
      });

      // Auto-select first device if available and none selected
      if (audioInputs.length > 0 && !audioinput) {
        onSaveMic(audioInputs[0].deviceId);
      } else if (audioinput) {
        startMicrophoneTest(audioinput);
      }

      if (audioOutputs.length > 0 && !audiooutput) {
        onSaveSpeaker(audioOutputs[0].deviceId);
      }
    } catch (error) {
      console.error('Error getting audio devices:', error);
      toast.error('Could not detect your audio devices');
    } finally {
      setIsLoading(false);
    }
  };

  const startMicrophoneTest = async (deviceId) => {
    try {
      if (micStream) {
        micStream.getTracks().forEach((track) => track.stop());
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: deviceId ? { exact: deviceId } : undefined },
      });
      setMicStream(stream);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Could not access the selected microphone');
    }
  };

  const testSpeakers = async () => {
    try {
      // Create an audio context and oscillator
      const audioContext = new AudioContext();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      // Configure the test sound
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(440, audioContext.currentTime); // A4 note
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime); // Lower volume

      oscillator.connect(gainNode);

      // Create an audio element to route the sound
      const audioElement = new Audio();
      const audioDestination = audioContext.createMediaStreamDestination();
      gainNode.connect(audioDestination);
      audioElement.srcObject = audioDestination.stream;

      // Set the sinkId for the selected speaker
      if (audioElement.setSinkId) {
        await audioElement.setSinkId(selectedSpeaker);
      } else {
        console.warn('setSinkId is not supported in this browser.');
      }

      // Play the test tone
      oscillator.start();
      audioElement.play();

      // Stop the tone after 1 second
      setTimeout(() => {
        oscillator.stop();
        audioContext.close();
      }, 1000);
    } catch (error) {
      console.error('Error testing speakers:', error);
      toast.error('Could not play the test sound');
    }
  };

  return (
    <TabContent onClose={onClose} title="Audio Settings">
      <Spin spinning={isLoading}>
        {devices && (
          <Box>
            <Select
              fullWidth
              transformValue
              menuPosition="fixed"
              hideSelectedOptions={false}
              value={selectedMic}
              onChange={onSaveMic}
              label="Microphone"
              options={devices.audioInputs.map((i) => ({
                value: i.deviceId,
                label: i.label || `Microphone ${i.deviceId.slice(0, 5)}...`,
              }))}
              sx={{
                '& .react-select__menu': { width: 300 },
              }}
            />
            <Box mb={2}>
              <MicrophoneLevel audioStream={micStream} />
            </Box>
            {isSinkSupported && (
              <Stack direction="row" alignItems="flex-end" spacing={1}>
                <Select
                  fullWidth
                  transformValue
                  menuPosition="fixed"
                  hideSelectedOptions={false}
                  value={selectedSpeaker}
                  onChange={onSaveSpeaker}
                  label="Speakers"
                  options={devices?.audioOutputs.map((i) => ({
                    value: i.deviceId,
                    label: i.label || `System Default (Built-in Speakers)`,
                  }))}
                  sx={{
                    '& .react-select__menu': { width: 300 },
                  }}
                />
                <Button onClick={testSpeakers}>Test</Button>
              </Stack>
            )}
          </Box>
        )}
      </Spin>
    </TabContent>
  );
};

export default AudioSettings;
